import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AnimatedBackground from "../AnimatedBackground";
import MaterialIcon from "@/components/ui/MaterialIcon";
import Container from "../Container";
import Footer from "../Footer";

const HELP_DESK_OPTIONS = [
  "Freshdesk",
  "Help Scout",
  "HubSpot",
  "Intercom",
  "Jira",
  "LiveAgent",
  "Salesforce",
  "ServiceNow",
  "Spiceworks",
  "SysAid",
  "Zendesk",
  "Zoho Desk",
  "Other",
];

const COMMS_OPTIONS = [
  "Chanty",
  "Discord",
  "Flock",
  "Google Chat",
  "Microsoft Teams",
  "Rocket.Chat",
  "Slack",
  "Other",
];

const FORMSUBMIT_URL = "https://formsubmit.co/ajax/4f54cf3d65f379eaf38be99fdde99313";

const GOOGLE_SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1K8uga3CPjCy4BWmsMwLjkDwnIzSoeCSfQZzCPisYrIo/edit?gid=840813956#gid=840813956";

const FreeLandingPage: React.FunctionComponent = () => {
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [helpDesk, setHelpDesk] = useState("");
  const [helpDeskOther, setHelpDeskOther] = useState("");
  const [commsSoftware, setCommsSoftware] = useState("");
  const [commsSoftwareOther, setCommsSoftwareOther] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(FORMSUBMIT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email,
          company_name: companyName,
          help_desk_software: helpDesk === "Other" ? helpDeskOther : helpDesk,
          comms_software: commsSoftware === "Other" ? commsSoftwareOther : commsSoftware,
          team_size: teamSize,
          _subject: "Free Punch Cards Calculator Download",
          _template: "table",
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSubmitted(true);
      } else {
        setError(
          data.message ||
            "Something went wrong submitting the form. Please try again."
        );
      }
    } catch {
      setError(
        "Unable to reach the server. Please check your connection and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Hero Section with Animated Background */}
      <AnimatedBackground fullHeight={false}>
        <Container className="pt-28 pb-36">
          <div className="mt-[10vh] mb-[10vh] max-w-4xl">
            <Heading level={1} variant="xl" className="mb-6 text-foreground">
              Get your free Punch Cards Calculator
            </Heading>
            <Text variant="xl" className="text-muted-foreground mb-8 max-w-2xl">
              Turn your existing support data into a clear, comparable
              performance score in minutes.
            </Text>
          </div>
        </Container>
      </AnimatedBackground>

      {/* Form Section on Highlight Background */}
      <section className="relative z-20 w-full bg-highlight pt-48 pb-24">
        {/* Form Card - Using negative margin to poke up from the background */}
        <div className="relative -mt-96 mb-20">
          <Container>
            <div className="max-w-2xl">
              <Card className="bg-card/95 backdrop-blur-sm shadow-xl">
                <CardContent>
                  {isSubmitted ? (
                    <div className="py-8 px-4">
                      <div className="w-24 h-24 mb-4 -ml-3 text-highlight flex items-center justify-start">
                        <MaterialIcon icon="calculate" size={96} weight={200} />
                      </div>
                      <Heading level={2} variant="md" className="mb-2 text-foreground">
                        Access the Calculator
                      </Heading>
                      <Text variant="md" className="text-muted-foreground mb-8">
                        Make a copy of the Calculator and start scoring your team today.
                      </Text>
                      <Button
                        asChild
                        variant="highlight"
                        size="xl"
                      >
                        <a
                          href={GOOGLE_SHEET_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open Calculator
                          <MaterialIcon icon="open_in_new" size={20} className="ml-2" />
                        </a>
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Heading level={2} variant="md" className="mb-2 text-foreground">
                        Free Calculator
                      </Heading>
                      <Text variant="md" className="text-muted-foreground mb-6">
                        Enter your details below and we&apos;ll give you
                        instant access to the spreadsheet.
                      </Text>

                      {error && (
                        <div className="rounded-md bg-destructive/10 border border-destructive/20 p-4 mb-6">
                          <Text variant="sm" className="text-destructive">
                            {error}
                          </Text>
                        </div>
                      )}

                      <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                          <Label htmlFor="email">Company Email</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="you@company.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="companyName">Company Name</Label>
                          <Input
                            id="companyName"
                            type="text"
                            placeholder="Your company name"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Help Desk Software</Label>
                          <Select
                            value={helpDesk}
                            onValueChange={(value) => {
                              setHelpDesk(value);
                              if (value !== "Other") setHelpDeskOther("");
                            }}
                            required
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select your help desk" />
                            </SelectTrigger>
                            <SelectContent>
                              {HELP_DESK_OPTIONS.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {helpDesk === "Other" && (
                            <Input
                              type="text"
                              placeholder="Enter your Help Desk Software"
                              value={helpDeskOther}
                              onChange={(e) => setHelpDeskOther(e.target.value)}
                              required
                            />
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label>Communication Software</Label>
                          <Select
                            value={commsSoftware}
                            onValueChange={(value) => {
                              setCommsSoftware(value);
                              if (value !== "Other") setCommsSoftwareOther("");
                            }}
                            required
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select your Communication Software" />
                            </SelectTrigger>
                            <SelectContent>
                              {COMMS_OPTIONS.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {commsSoftware === "Other" && (
                            <Input
                              type="text"
                              placeholder="Enter your Communication Software"
                              value={commsSoftwareOther}
                              onChange={(e) => setCommsSoftwareOther(e.target.value)}
                              required
                            />
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="teamSize">
                            No. of Customer Service Agents
                          </Label>
                          <Input
                            id="teamSize"
                            type="number"
                            placeholder="e.g. 12"
                            value={teamSize}
                            onChange={(e) => setTeamSize(e.target.value)}
                            required
                            min="1"
                          />
                        </div>

                        <div className="pt-2">
                          <Button
                            type="submit"
                            variant="highlight"
                            size="xl"
                            disabled={isSubmitting}
                          >
                            {isSubmitting
                              ? "Submitting\u2026"
                              : "Access Free Calculator"}
                          </Button>
                        </div>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </Container>
        </div>

        {/* What's Inside Section */}
        <Container className="text-highlight-foreground">
          <Heading level={2} variant="lg" className="mb-8 max-w-4xl">
            What&apos;s in the Calculator?
          </Heading>
          <Text variant="xl" className="mb-12 max-w-4xl font-light">
            A ready-to-use spreadsheet that lets you:
          </Text>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            <div>
              <div className="w-12 h-12 mb-4 text-highlight-foreground flex items-center justify-start">
                <MaterialIcon icon="edit_note" size={48} weight={200} />
              </div>
              <Text variant="lg">
                Enter agent activity and CSAT data
              </Text>
            </div>

            <div>
              <div className="w-12 h-12 mb-4 text-highlight-foreground flex items-center justify-start">
                <MaterialIcon icon="calculate" size={48} weight={200} />
              </div>
              <Text variant="lg">
                Automatically calculate Total Points and Points per Hour
              </Text>
            </div>

            <div>
              <div className="w-12 h-12 mb-4 text-highlight-foreground flex items-center justify-start">
                <MaterialIcon icon="compare_arrows" size={48} weight={200} />
              </div>
              <Text variant="lg">
                Compare agents on a fair, calibrated scorecard
              </Text>
            </div>

            <div>
              <div className="w-12 h-12 mb-4 text-highlight-foreground flex items-center justify-start">
                <MaterialIcon icon="tune" size={48} weight={200} />
              </div>
              <Text variant="lg">
                Test &ldquo;what if&rdquo; scenarios before making changes
              </Text>
            </div>
          </div>

          <Text variant="md" className="text-highlight-foreground/80">
            Built for teams using Intercom, Zendesk, Freshdesk, Salesforce, or
            exported CSVs.
          </Text>
        </Container>
      </section>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default FreeLandingPage;
